import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';

const Projects = () => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(AuthContext);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchProjects();
  }, [i18n.language]);

  useEffect(() => {
    filterProjects();
  }, [searchTerm, statusFilter, projects]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { params: { lang: i18n.language } });
      setProjects(response.data);
      setFilteredProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = projects;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProjects(filtered);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12" data-testid="projects-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 animate-fadeIn">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">{t('projects.title')}</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('projects.subtitle')}
          </p>
        </div>

        {/* Filters */}
        <div className="card mb-8 animate-slideUp">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <Input
                  type="text"
                  placeholder={t('projects.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('all')}
                data-testid="filter-all"
              >
                {t('projects.filterAll')}
              </Button>
              <Button
                variant={statusFilter === 'active' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('active')}
                className={statusFilter === 'active' ? 'bg-green-600 hover:bg-green-700' : ''}
                data-testid="filter-active"
              >
                {t('projects.filterActive')}
              </Button>
              <Button
                variant={statusFilter === 'upcoming' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('upcoming')}
                className={statusFilter === 'upcoming' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                data-testid="filter-upcoming"
              >
                {t('projects.filterUpcoming')}
              </Button>
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-pulse text-lg text-slate-600">{t('projects.loading')}</div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600">{t('projects.noProjects')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-slideUp">
            {filteredProjects.map((project, index) => (
              <div key={project.id} className="project-card" data-testid={`project-card-${index}`}>
                <div className="relative">
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="w-full h-48 object-cover"
                  />
                  {/* Project Badge - Top Right Corner */}
                  {project.badge_enabled && project.badge_image_url && (
                    <img
                      src={project.badge_image_url}
                      alt="Badge"
                      className="absolute top-1 right-1 w-14 h-14 object-contain drop-shadow-lg z-10 transition-all duration-300 ease-in-out hover:scale-[2] cursor-pointer"
                      style={{ maxWidth: '56px', maxHeight: '56px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`badge ${project.status === 'active' ? 'badge-green' : 'badge-blue'}`}>
                      {project.status}
                    </span>
                    <span className="text-sm text-slate-500">{project.location}</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{project.title}</h3>
                  <p className="text-slate-600 mb-4 line-clamp-2">{project.description}</p>
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div>
                      <p className="text-slate-500">{t('projects.capacity')}</p>
                      <p className="font-semibold text-slate-900">{project.capacity_mw} MWh</p>
                    </div>
                    <div>
                      <p className="text-slate-500">{t('projects.returnPa')}</p>
                      <p className="font-semibold text-green-600">{project.expected_return}%</p>
                    </div>
                    <div>
                      <p className="text-slate-500">{t('projects.pricePerShare')}</p>
                      <p className="font-semibold text-slate-900">€{project.price_per_share.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">{t('projects.duration')}</p>
                      <p className="font-semibold text-slate-900">{project.duration_years} {t('projects.years')}</p>
                    </div>
                  </div>

                  {/* Project Progress Bar - Only visible for logged-in users */}
                  {user && project.project_progress !== undefined && project.project_progress > 0 && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">{t('projects.projectProgress')}</span>
                        <span className="font-semibold text-green-600">{project.project_progress.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(project.project_progress, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">{t('projects.availableShares')}</span>
                      <span className="font-semibold">{project.available_shares}/{project.total_shares}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-green-500 to-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${(project.available_shares / project.total_shares) * 100}%` }}
                      />
                    </div>
                  </div>
                  <Link to={`/bess/projects/${project.id}`}>
                    <Button className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white" data-testid={`view-details-btn-${index}`}>
                      {t('projects.viewDetails')}
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
